import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import {
  Search,
  // Upload,
  Briefcase,
  Users2,
  Headphones,
  Loader2,
} from 'lucide-react';
import { API, graphqlOperation } from 'aws-amplify';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { PageHeader } from '../../components/page-header';
import { KanbanColumn } from '../../components/pipeline/KanbanColumn';
import { PipelineListView } from '../../components/pipeline/PipelineListView';
import { DealDetailPanel } from '../../components/pipeline/DealDetailPanel';
import { ChatSidePanel } from '../../components/pipeline/ChatSidePanel';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useToast } from '../../components/ui/toast';
import { usePipeline } from '../../hooks/use-pipeline';
import { Deal } from '../../types/pipeline.types';
import { cn } from '../../lib/utils';
import { updateDeal } from '../../../graphql/mutations';

declare const global: {
  me: {
    imageName?: string;
    name: string;
  };
};

export function PipelinePage() {
  const navigate = useNavigate();
  const { dealId: urlDealId } = useParams<{ dealId?: string }>();
  const {
    dealTypes,
    categoryId,
    dealStages,
    dealsByStage,
    dealPeople,
    isLoading,
    isPeopleLoading,
    searchString,
    setSearchString,
    selectedOwnerId,
    setSelectedOwnerId,
    moveDeal,
    changeSortOrder,
    changeCategory,
    loadMore,
  } = usePipeline();

  const [selectedDealId, setSelectedDealId] = React.useState<string | null>(
    null
  );
  const [isDealPanelOpen, setIsDealPanelOpen] = React.useState(false);
  const [isChatPanelOpen, setIsChatPanelOpen] = React.useState(false);
  const [isDealExpanded, setIsDealExpanded] = React.useState(false);
  const [chatTaskListId, setChatTaskListId] = React.useState<string | null>(
    null
  );
  const [viewMode, setViewMode] = React.useState<'board' | 'list'>('board');

  // Open deal panel from URL param (e.g. /v2/pipeline/:dealId)
  React.useEffect(() => {
    if (urlDealId) {
      setSelectedDealId(urlDealId);
      setIsDealPanelOpen(true);
    }
  }, [urlDealId]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { draggableId, source, destination } = result;
    if (source.droppableId !== destination.droppableId) {
      moveDeal(draggableId, source.droppableId, destination.droppableId);
    }
  };

  const handleNewDeal = () => {
    navigate('/v2/pipeline/new');
  };

  const handleDealClick = (deal: Deal) => {
    setSelectedDealId(deal.id);
    setIsDealPanelOpen(true);
    setIsDealExpanded(false);
    navigate(`/v2/pipeline/${deal.id}`, { replace: true });
  };

  const handleCloseDealPanel = () => {
    setIsDealPanelOpen(false);
    setIsChatPanelOpen(false);
    setIsDealExpanded(false);
    setTimeout(() => setSelectedDealId(null), 300);
    navigate('/v2/pipeline', { replace: true });
  };

  const handleEditDeal = (dealId: string) => {
    navigate(`/v2/pipeline/${dealId}/edit`);
  };

  const handleAITabClick = (_dealId: string, taskListId: string) => {
    setChatTaskListId(taskListId);
    setIsChatPanelOpen(true);
  };

  const handleCloseChatPanel = () => {
    setIsChatPanelOpen(false);
    setChatTaskListId(null);
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deletingDealId, setDeletingDealId] = React.useState<string | null>(
    null
  );
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [stageChangeOpen, setStageChangeOpen] = React.useState(false);
  const [changingStageDealId, setChangingStageDealId] = React.useState<
    string | null
  >(null);
  const [changingStageFromId, setChangingStageFromId] = React.useState<
    string | null
  >(null);
  // Track the latest stage per deal after changes (since DealDetailPanel caches stale data)
  const latestStageByDeal = React.useRef<Record<string, string>>({});
  const toast = useToast();

  const handleDeleteDeal = (dealId: string) => {
    setDeletingDealId(dealId);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteDeal = async () => {
    if (!deletingDealId) {
      return;
    }
    setIsDeleting(true);
    try {
      await API.graphql(
        graphqlOperation(updateDeal, {
          input: { id: deletingDealId, isDeleted: true },
        })
      );
      toast.success('Deal deleted successfully');
      setDeleteConfirmOpen(false);
      setDeletingDealId(null);
      handleCloseDealPanel();
      // Trigger a refresh by changing search and back
      setSearchString(searchString + ' ');
      setTimeout(() => setSearchString(searchString), 100);
    } catch (err) {
      console.error('Error deleting deal:', err);
      toast.error('Failed to delete deal');
    } finally {
      setIsDeleting(false);
    }
  };

  const headerActions = [
    // {
    //   id: 'import',
    //   label: 'Import',
    //   icon: <Upload className="size-4" />,
    //   variant: 'secondary' as const,
    // },
    {
      id: 'create',
      label: 'Add deal',
      icon: <span className="text-base leading-none">+</span>,
      variant: 'primary' as const,
      onClick: handleNewDeal,
    },
  ];

  const currentTitle =
    categoryId && dealTypes[categoryId]
      ? `${dealTypes[categoryId].title} Pipeline`
      : 'Pipeline';

  const renderTabs = () => {
    const types = Object.values(dealTypes);
    if (types.length <= 1) return null;

    return (
      <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start h-auto p-0 gap-6">
        {types.map((type, index) => (
          <TabsTrigger
            key={type.id}
            value={type.id}
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 pt-0 gap-2"
          >
            {index === 0 && <Briefcase className="size-4" />}
            {index === 1 && <Users2 className="size-4" />}
            {index === 2 && <Headphones className="size-4" />}
            {type.title}
          </TabsTrigger>
        ))}
      </TabsList>
    );
  };

  const renderFilters = () => (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Board / List toggle */}
      <div className="flex items-center border border-border rounded-md overflow-hidden">
        <button
          className={cn(
            'px-3 py-1.5 text-sm font-medium transition-colors',
            viewMode === 'board'
              ? 'bg-primary text-primary-foreground'
              : 'bg-background text-muted-foreground hover:text-foreground'
          )}
          onClick={() => setViewMode('board')}
        >
          Board
        </button>
        <button
          className={cn(
            'px-3 py-1.5 text-sm font-medium transition-colors',
            viewMode === 'list'
              ? 'bg-primary text-primary-foreground'
              : 'bg-background text-muted-foreground hover:text-foreground'
          )}
          onClick={() => setViewMode('list')}
        >
          List
        </button>
      </div>

      <div className="relative flex-1 min-w-[280px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by deal, customer, address..."
          className="pl-10"
          value={searchString}
          onChange={(e) => setSearchString(e.target.value)}
        />
      </div>

      <Select
        value={selectedOwnerId}
        onValueChange={setSelectedOwnerId}
        disabled={isPeopleLoading}
      >
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="All people" />
        </SelectTrigger>
        <SelectContent>
          {dealPeople.map((person) => (
            <SelectItem key={person.id} value={person.id}>
              {person.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const renderKanbanBoard = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      );
    }

    if (!dealStages.length) {
      return (
        <div className="flex items-center justify-center py-20 text-muted-foreground">
          No pipeline stages configured for this category.
        </div>
      );
    }

    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto overflow-y-auto pb-4 h-full max-w-full">
          {dealStages.map((stage) => (
            <KanbanColumn
              key={stage.id}
              stage={stage}
              data={
                dealsByStage[stage.id] || {
                  deals: [],
                  sortOrder: 'DESC',
                  currentOffset: 0,
                  canLoadMore: false,
                  isLoading: true,
                }
              }
              onDealClick={handleDealClick}
              onEditDeal={handleEditDeal}
              onDeleteDeal={(dealId) => handleDeleteDeal(dealId)}
              onSortChange={changeSortOrder}
              onLoadMore={loadMore}
            />
          ))}
        </div>
      </DragDropContext>
    );
  };

  const renderListView = () => {
    return (
      <PipelineListView
        dealStages={dealStages}
        dealsByStage={dealsByStage}
        isLoading={isLoading}
        onDealClick={handleDealClick}
        onEditDeal={handleEditDeal}
        onLoadMore={loadMore}
      />
    );
  };

  const renderContent = () => {
    if (viewMode === 'list') {
      return renderListView();
    }
    return renderKanbanBoard();
  };

  const types = Object.values(dealTypes);
  const hasTabs = types.length > 1;

  return (
    <div
      className="h-full w-full"
      style={{
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        overflow: 'hidden',
      }}
    >
      {/* Top section: header + filters - never scrolls */}
      <div>
        <PageHeader
          title={currentTitle}
          actions={headerActions}
          avatar={{
            src: global.me?.imageName || undefined,
            name: global.me?.name,
          }}
        />
        {hasTabs && (
          <Tabs value={categoryId} onValueChange={changeCategory}>
            <div className="px-6 pt-4">{renderTabs()}</div>
          </Tabs>
        )}
        <div className="px-6 py-4">{renderFilters()}</div>
      </div>

      {/* Bottom section: kanban/list - scrolls both directions independently */}
      <div style={{ overflow: 'auto' }} className="px-6 pb-6">
        {renderContent()}
      </div>

      <DealDetailPanel
        dealId={selectedDealId}
        isOpen={isDealPanelOpen}
        onClose={handleCloseDealPanel}
        onEdit={handleEditDeal}
        onDelete={handleDeleteDeal}
        onAITabClick={handleAITabClick}
        isChatOpen={isChatPanelOpen}
        isExpanded={isDealExpanded}
        onToggleExpand={() => setIsDealExpanded((prev) => !prev)}
        onChangeStage={(dealId: string, currentStageId?: string) => {
          setChangingStageDealId(dealId);
          // Use the locally tracked stage if available (handles the case where
          // the deal detail panel still has stale data after a previous change)
          const effectiveStageId =
            latestStageByDeal.current[dealId] || currentStageId || null;
          setChangingStageFromId(effectiveStageId);
          setStageChangeOpen(true);
        }}
      />

      <ChatSidePanel
        dealId={selectedDealId}
        taskListId={chatTaskListId}
        isOpen={isChatPanelOpen}
        onClose={handleCloseChatPanel}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setDeletingDealId(null);
        }}
        onConfirm={confirmDeleteDeal}
        title="Delete this deal?"
        description="This action cannot be undone. The deal will be permanently removed."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Stage Change Dialog */}
      <Dialog open={stageChangeOpen} onOpenChange={setStageChangeOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Stage</DialogTitle>
          </DialogHeader>
          <div className="space-y-1 mt-2 max-h-[60vh] overflow-auto">
            {dealStages.map((stage) => {
              const isCurrent = stage.id === changingStageFromId;
              return (
                <button
                  key={stage.id}
                  disabled={isCurrent}
                  onClick={async () => {
                    if (!changingStageDealId || !changingStageFromId) {
                      return;
                    }
                    try {
                      await moveDeal(
                        changingStageDealId,
                        changingStageFromId,
                        stage.id
                      );
                      // Track the new stage locally so subsequent opens show correct "current"
                      latestStageByDeal.current[changingStageDealId] = stage.id;
                      toast.success(`Stage changed to ${stage.label}`);
                    } catch (err) {
                      console.error('Error changing stage:', err);
                      toast.error('Failed to change stage');
                    }
                    setStageChangeOpen(false);
                    setChangingStageDealId(null);
                    setChangingStageFromId(null);
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors',
                    isCurrent
                      ? 'bg-primary/10 text-primary font-semibold cursor-default'
                      : 'hover:bg-muted/50 text-foreground'
                  )}
                >
                  {stage.label}
                  {isCurrent && (
                    <span className="ml-2 text-xs text-primary/70">
                      (current)
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
